import { useCallback, useMemo } from 'react';
import { usePermissionsStateContext } from 'providers/Auth';

import { PermissionMap, canGrantSection } from 'utils/ceiling';
import useIsSuperAdminRole from './useIsSuperAdminRole';

/**
 * The operator's own permission ceiling.
 *
 * Role writes used to be super_admin-only. They are now open to every admin,
 * bounded by what that admin holds — "nobody may grant what they do not hold"
 * (DELEGATED ADMIN, decided 2026-10-29). So the panel can no longer gate these
 * controls on a role name; it has to ask whether THIS operator may grant THIS
 * set.
 *
 * The map comes from /auth/me via the Auth provider, keyed by section. That is
 * the same source the backend reads its ceiling from — the token — so the two
 * agree by construction. It is safe to trust a token-aged ceiling only because
 * changing what someone holds now ends their sessions; if that is ever removed
 * this becomes stale-readable for up to JWT_EXPIRATION.
 *
 * ONLY ONE QUESTION IS LEFT HERE. "May I act on this role", "may I assign
 * it" and "may I change this admin's role" are all decided by the server now
 * and ride on the row — see roleEditRefusal, roleAssignRefusal and
 * adminActionRefusal in utils/ceiling. What remains is per-CHECKBOX: whether
 * a single bit may be ticked in the role editor. There is no flag per bit and
 * there should not be one, so this stays ours.
 */
const useCeiling = () => {
  const permissions = usePermissionsStateContext() as PermissionMap;
  const isSuperAdmin = useIsSuperAdminRole();

  const canGrant = useCallback(
    (section: number, mask: number | string) =>
      isSuperAdmin || canGrantSection(permissions, section, mask),
    [isSuperAdmin, permissions]
  );

  return useMemo(
    () => ({
      /** The operator's own section -> mask map. */
      permissions,
      isSuperAdmin,
      /** May the operator put this bit in a role? */
      canGrant
    }),
    [canGrant, isSuperAdmin, permissions]
  );
};

export default useCeiling;
